import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import cc from 'classcat';
import d from 'dayjs';
import { useKey, useLockBodyScroll } from 'react-use';
import styles from './gallerymodal.module.sass';
import { NextThumbArrow, PrevThumbArrow } from './components/Arrows/Arrows';
import { Badge } from '../Badge';
import { Comment } from '../Comment';
import { useLimitList } from '../../hooks/useLimitList';
import { Icon } from '../Icon';
import { IconSVG } from '../SpriteSVG';
import { Image } from '../Image/Image';
// import { YaAndGaCruisePage } from '../../lib/YaAndGaHelper';

const Hover = ({ open, ...props }) => (
  <div className={cc([styles.overlay, { [styles.open]: open }])} {...props} />
);

const Thumb = ({ selected, onClick, imgSrc, description }) => (
  <div
    className={cc([
      styles.emblaSlide,
      styles.emblaSlideThumb,
      { [styles.isSelected]: selected },
    ])}
  >
    <button
      onClick={onClick}
      className={cc([styles.emblaSlideInner, styles.emblaSlideInnerThumb])}
      type="button"
    >
      <Image
        className={styles.emblaSlideThumbnail}
        src={imgSrc.src}
        srcSet={imgSrc.srcset}
        alt={description ?? ''}
        loading="lazy"
        title={description ?? ''}
      />
    </button>
  </div>
);

export const GalleryModal = ({
  category: { name, type } = { name: '', type: '' },
  maxi,
  mini,
  reviews = [],
  activeImg,
  onClose,
  isMobile,
  onCloseAction,
  onActionClose,
  onNextArrow,
  onPrevArrow,
  onArrowPrev,
  onArrowNext,
  onClickImg,
  onImgClick,
  onClickMore,
  onMoreClick,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(activeImg);
  const [thumbViewportRef, emblaThumbs] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    selectedClass: '',
    dragFree: true,
    startIndex: activeImg,
  });
  const { show, isCanShowMore, onShowMore } = useLimitList({
    count: reviews.length,
  });
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const isOpen = activeImg !== null;
  const isSidebar = reviews.length > 0;

  const scrollPrev = useCallback(
    () => emblaThumbs && emblaThumbs.scrollPrev(),
    [emblaThumbs],
  );
  const scrollNext = useCallback(
    () => emblaThumbs && emblaThumbs.scrollNext(),
    [emblaThumbs],
  );

  const onThumbClick = useCallback(
    index => {
      if (!emblaThumbs) return;
      if (emblaThumbs.clickAllowed()) emblaThumbs.scrollTo(index);
    },
    [emblaThumbs],
  );

  const onSelect = useCallback(() => {
    if (!emblaThumbs) return;
    setSelectedIndex(emblaThumbs.selectedScrollSnap());
    setPrevBtnEnabled(emblaThumbs.canScrollPrev());
    setNextBtnEnabled(emblaThumbs.canScrollNext());
    emblaThumbs.scrollTo(emblaThumbs.selectedScrollSnap());
  }, [emblaThumbs, setSelectedIndex]);

  useEffect(() => {
    if (!emblaThumbs) return;
    onSelect();
    emblaThumbs.on('select', onSelect);
  }, [emblaThumbs, onSelect]);

  const [openInfoPhoto, setOpenInfoPhoto] = useState(false);
  const closeHandler = () => [onClose(null), setOpenInfoPhoto(false)];

  useKey('Escape', closeHandler);
  useKey('ArrowLeft', () => emblaThumbs.scrollPrev(), {}, [emblaThumbs]);
  useKey('ArrowRight', () => emblaThumbs.scrollNext(), {}, [emblaThumbs]);

  useLockBodyScroll(isOpen);

  return (
    <>
      <Hover open={isOpen} onClick={closeHandler} />
      <div className={cc([styles.modal, { [styles.open]: isOpen }])}>
        <div className={styles.gallery}>
          {isOpen && (
            <div
              className={cc([
                styles.embla,
                styles.mainEmbla,
                { [styles.short]: !isSidebar },
              ])}
            >
              {maxi.map((i, index) => (
                <div
                  className={cc([
                    styles.emblaSlideInner,
                    styles.emblaSlideMain,
                    { [styles.hidden]: index !== selectedIndex },
                  ])}
                >
                  <Image
                    className={styles.emblaSlideImg}
                    src={i.url.src}
                    srcSet={i.url.srcset}
                    alt={i?.description ?? ''}
                    loading="lazy"
                    title={i?.description ?? ''}
                  />
                </div>
              ))}
              <PrevThumbArrow
                onClick={() => {
                  scrollPrev();
                  if (onPrevArrow) {
                    onPrevArrow();
                  }
                  if (onArrowPrev) {
                    onArrowPrev();
                  }
                }}
                enabled={prevBtnEnabled}
              />
              <NextThumbArrow
                onClick={() => {
                  scrollNext();
                  if (onNextArrow) {
                    onNextArrow();
                  }
                  if (onArrowNext) {
                    onArrowNext();
                  }
                }}
                enabled={nextBtnEnabled}
              />
            </div>
          )}
          <div className={styles.footer}>
            <div className={styles.head}>
              <span className={styles.count}>
                {selectedIndex + 1} из {mini?.length}
              </span>
              {maxi?.map((i, index) => (
                <div
                  key={index}
                  className={cc([{ [styles.hidden]: index !== selectedIndex }])}
                >
                  {i.description}
                </div>
              ))}
            </div>
            <div className={cc([styles.embla, styles.emblaThumb])}>
              <div className={styles.emblaViewport} ref={thumbViewportRef}>
                <div
                  className={cc([
                    styles.emblaContainer,
                    styles.emblaContainerThumb,
                  ])}
                >
                  {mini?.map((i, index) => (
                    <Thumb
                      onClick={() => {
                        onThumbClick(index);
                        if (onClickImg) {
                          onClickImg();
                        }
                        if (onImgClick) {
                          onImgClick();
                        }
                      }}
                      selected={index === selectedIndex}
                      imgSrc={i.url}
                      key={index}
                      description={i.description}
                    />
                  ))}
                </div>
              </div>
            </div>
            {!isMobile && (
              <div className={styles.infoPhoto}>
                <div className={styles.infoPhotoHead}>
                  <button
                    className={styles.infoPhotoButton}
                    onClick={() => setOpenInfoPhoto(!openInfoPhoto)}
                  >
                    Информация о фотографиях
                    <div
                      className={
                        openInfoPhoto
                          ? styles.infoPhotoInsideReverse
                          : styles.infoPhotoInside
                      }
                    />
                  </button>
                </div>

                {openInfoPhoto && (
                  <div className={styles.infoPhotoOpen}>
                    Обратите внимание, что представленные фотографии
                    соответствуют категориям кают, но являются базовыми. В
                    зависимости от номера каюты интерьер может немного
                    отличаться.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {reviews && reviews.length > 0 && (
          <div className={styles.reviews}>
            <div className={styles.head}>
              {name && type && (
                <Badge view={type} icon={type}>
                  {name}
                </Badge>
              )}
              <Badge view="rewies" icon="reviews">
                {String(reviews.length)} отзывов
              </Badge>
            </div>
            <div className={styles.scrollBar}>
              {reviews.slice(0, show).map((i, index) => (
                <Comment
                  isMobile={isMobile}
                  key={index}
                  userPick={
                    i.user_img &&
                    `${process.env.NEXT_PUBLIC_BASE_URL}/${i.user_img}`
                  }
                  name={i.username}
                  date={d(i.r_date).format('DD MMMM YYYY')}
                  className={styles.commentWrapper}
                  isModal
                >
                  {i.review}
                </Comment>
              ))}
              {isCanShowMore && (
                <button
                  className={styles.showMore}
                  onClick={() => {
                    onShowMore();
                    if (onClickMore) {
                      onClickMore();
                    }
                    if (onMoreClick) {
                      onMoreClick();
                    }
                  }}
                >
                  Показать еще
                </button>
              )}
            </div>
          </div>
        )}
        <button
          className={styles.close}
          onClick={() => {
            onClose(null);
            if (onCloseAction) {
              onCloseAction();
              setOpenInfoPhoto(false);
            }
            if (onActionClose) {
              onActionClose();
              setOpenInfoPhoto(false);
            }
          }}
        >
          <div className={styles.iconHolder}>
            <Icon
              icon={
                <IconSVG
                  name={'common/close-white'}
                  width={16}
                  height={16}
                  alt="Закрыть"
                />
              }
              title="Закрыть"
            />
          </div>
          {/* <img src={CloseIcon} alt="Закрыть" /> */}
        </button>
      </div>
    </>
  );
};
